import { useState, useRef, useEffect } from "react";
import { MessageCircle, X, Send, Loader2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

const FAQ_DATA = [
  { q: "What is PawLinx?", a: "PawLinx is a pet medication referral service. We help pet owners get affordable medication delivered to their door. You upload your prescription, we verify and fulfill it." },
  { q: "How does it work?", a: "It's simple: 1) Upload your pet's prescription, 2) We verify and process it, 3) Your medication is shipped directly to you. The whole process starts online and takes about 5 minutes to submit." },
  { q: "How much can I save?", a: "Pet owners save up to 30% compared to local pharmacies. Our pricing is transparent with no hidden fees, so you see the total before you pay." },
  { q: "Where do you ship?", a: "We ship to most addresses with reliable tracking. Enter your address during checkout to confirm." },
  { q: "How long does shipping take?", a: "After your prescription is verified, fulfillment and shipping typically take 3-7 business days depending on your location." },
  { q: "Is it safe?", a: "Absolutely. Every prescription is professionally verified before fulfillment. We work with trusted fulfillment partners and all payments are processed securely through Stripe." },
  { q: "Do I need an account?", a: "Yes, you'll need to create a free account to submit prescriptions and track your orders. It takes less than a minute to sign up." },
  { q: "How do I refill my prescription?", a: "Just log in to your account and submit your updated prescription. Since your info is already on file, it's even faster the second time." },
  { q: "What file types do you accept?", a: "We accept prescription uploads in JPEG, PNG, and PDF format, up to 10MB in size." },
  { q: "What if there's a problem with my prescription?", a: "If any issue is found during verification, we'll notify you right away with a clear explanation. Our team will help resolve it or issue a full refund." },
];

const INITIAL_MESSAGES = [
  { from: "bot", text: "Hi there! Welcome to PawLinx. I can help answer your questions about our service. What would you like to know?" },
];

function findAnswer(input) {
  const lower = input.toLowerCase();
  const keywords = {
    "what is": 0, "about pawlinx": 0, "tell me about": 0,
    "how does": 1, "how it work": 1, "process": 1, "steps": 1,
    "save": 2, "cost": 2, "price": 2, "pricing": 2, "how much": 2, "afford": 2, "cheap": 2, "expensive": 2,
    "ship": 3, "deliver": 3, "us and canada": 3, "canada": 3, "united states": 3, "where": 3,
    "how long": 4, "time": 4, "days": 4, "fast": 4, "when": 4,
    "safe": 5, "secure": 5, "trust": 5, "legit": 5, "reliable": 5,
    "account": 6, "sign up": 6, "register": 6, "log in": 6, "login": 6,
    "refill": 7, "reorder": 7, "again": 7,
    "file": 8, "format": 8, "upload": 8, "jpeg": 8, "pdf": 8, "photo": 8, "type": 8,
    "problem": 9, "issue": 9, "wrong": 9, "reject": 9, "refund": 9, "help": 9,
  };
  for (const [kw, idx] of Object.entries(keywords)) {
    if (lower.includes(kw)) return FAQ_DATA[idx].a;
  }
  return null;
}

export default function ChatBot() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState(INITIAL_MESSAGES);
  const [input, setInput] = useState("");
  const [showContactForm, setShowContactForm] = useState(false);
  const [contactForm, setContactForm] = useState({ name: "", email: "", phone: "" });
  const [submittingContact, setSubmittingContact] = useState(false);
  const [contactSubmitted, setContactSubmitted] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, showContactForm]);

  const addBot = (text) => setMessages(prev => [...prev, { from: "bot", text }]);

  const handleSend = () => {
    const trimmed = input.trim();
    if (!trimmed) return;
    setMessages(prev => [...prev, { from: "user", text: trimmed }]);
    setInput("");

    const talkLower = trimmed.toLowerCase();
    if (talkLower.includes("talk to") || talkLower.includes("speak to") || talkLower.includes("real person") || talkLower.includes("human") || talkLower.includes("contact") || talkLower.includes("call") || talkLower.includes("reach")) {
      setTimeout(() => {
        addBot("I'd be happy to connect you with our team! Please share your details below and we'll reach out within 4 hours.");
        setShowContactForm(true);
      }, 500);
      return;
    }

    const answer = findAnswer(trimmed);
    setTimeout(() => {
      if (answer) {
        addBot(answer);
        setTimeout(() => addBot("Anything else I can help with? Or if you'd like to speak with someone, just say \"talk to someone\"."), 1200);
      } else {
        addBot("I'm not sure about that one. Would you like to speak with our team? Just say \"talk to someone\" and I'll collect your info. We'll get back to you within 4 hours.");
      }
    }, 600);
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    if (!contactForm.name || !contactForm.email) return;
    setSubmittingContact(true);
    try {
      await api.post("/chat/contact", contactForm);
      setContactSubmitted(true);
      setShowContactForm(false);
      addBot(`Thanks ${contactForm.name.split(" ")[0]}! We've got your info. Our team will reach out to ${contactForm.email} within 4 hours. Is there anything else I can help with in the meantime?`);
    } catch {
      // Don't claim success when the lead never reached the server.
      addBot(`Sorry ${contactForm.name.split(" ")[0]}, something went wrong sending your info. Please try again in a moment, or email us directly at support@pawlinx.com.`);
    }
    setSubmittingContact(false);
  };

  return (
    <>
      {/* Toggle button */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-4 right-4 z-50 w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-paw-primary hover:bg-paw-primary-hover text-white shadow-xl shadow-paw-primary/20 flex items-center justify-center transition-all hover:scale-105"
          data-testid="chatbot-toggle"
        >
          <MessageCircle className="w-5 h-5 sm:w-6 sm:h-6" strokeWidth={1.5} />
        </button>
      )}

      {/* Chat window */}
      {open && (
        <div className="fixed bottom-0 right-0 sm:bottom-5 sm:right-5 z-50 w-full sm:w-[380px] sm:max-w-[calc(100vw-40px)] bg-white border border-paw-border sm:rounded-2xl shadow-2xl flex flex-col overflow-hidden" style={{ height: "min(520px, 100vh)" }} data-testid="chatbot-window">
          {/* Header */}
          <div className="bg-paw-primary px-5 py-4 flex items-center justify-between flex-shrink-0">
            <div>
              <p className="font-outfit font-semibold text-[15px] text-white">PawLinx Support</p>
              <p className="font-manrope text-[11px] text-white/50">Usually responds instantly</p>
            </div>
            <button onClick={() => setOpen(false)} className="w-8 h-8 rounded-full hover:bg-white/10 flex items-center justify-center transition-colors" data-testid="chatbot-close">
              <X className="w-4 h-4 text-white/70" />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-paw-bg">
            {messages.map((msg, i) => (
              <div key={i} className={`flex ${msg.from === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[80%] px-4 py-2.5 rounded-2xl font-manrope text-[13px] leading-relaxed ${
                  msg.from === "user"
                    ? "bg-paw-primary text-white rounded-br-md"
                    : "bg-white border border-paw-border text-paw-text rounded-bl-md"
                }`}>
                  {msg.text}
                </div>
              </div>
            ))}

            {/* Quick questions */}
            {messages.length <= 2 && !showContactForm && (
              <div className="space-y-1.5 pt-2">
                <p className="font-manrope text-[11px] text-paw-text-muted px-1">Quick questions:</p>
                {["How does it work?", "How much can I save?", "Do you ship to the US?", "Talk to someone"].map((q, i) => (
                  <button key={i} onClick={() => { setInput(q); setTimeout(() => { setMessages(prev => [...prev, { from: "user", text: q }]); setInput(""); const a = findAnswer(q); if (q === "Talk to someone") { setTimeout(() => { addBot("I'd be happy to connect you with our team! Please share your details below and we'll reach out within 4 hours."); setShowContactForm(true); }, 500); } else if (a) { setTimeout(() => { addBot(a); setTimeout(() => addBot("Anything else I can help with?"), 1000); }, 500); } }, 0); }}
                    className="block w-full text-left px-3 py-2 rounded-xl bg-white border border-paw-border text-[12px] font-manrope text-paw-text-secondary hover:border-paw-primary/30 hover:text-paw-text transition-colors"
                    data-testid={`chatbot-quick-${i}`}
                  >
                    {q}
                  </button>
                ))}
              </div>
            )}

            {/* Contact form */}
            {showContactForm && !contactSubmitted && (
              <form onSubmit={handleContactSubmit} className="bg-white border border-paw-border rounded-xl p-4 space-y-3" data-testid="chatbot-contact-form">
                <p className="font-manrope text-[12px] font-semibold text-paw-text">Your details</p>
                <input required value={contactForm.name} onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })} placeholder="Full name *" className="w-full h-9 px-3 rounded-lg border border-paw-border text-[13px] font-manrope focus:outline-none focus:border-paw-primary" data-testid="chatbot-contact-name" />
                <input required type="email" value={contactForm.email} onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })} placeholder="Email address *" className="w-full h-9 px-3 rounded-lg border border-paw-border text-[13px] font-manrope focus:outline-none focus:border-paw-primary" data-testid="chatbot-contact-email" />
                <input value={contactForm.phone} onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })} placeholder="Phone (optional)" className="w-full h-9 px-3 rounded-lg border border-paw-border text-[13px] font-manrope focus:outline-none focus:border-paw-primary" data-testid="chatbot-contact-phone" />
                <button type="submit" disabled={submittingContact} className="w-full bg-paw-accent hover:bg-paw-accent-hover text-white rounded-lg h-9 text-[13px] font-manrope font-semibold transition-colors flex items-center justify-center gap-1.5" data-testid="chatbot-contact-submit">
                  {submittingContact ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Submit"}
                </button>
              </form>
            )}

            <div ref={bottomRef} />
          </div>

          {/* Input */}
          <div className="border-t border-paw-border px-3 py-3 flex gap-2 bg-white flex-shrink-0">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSend()}
              placeholder="Ask a question..."
              className="flex-1 h-9 px-3 rounded-lg bg-paw-surface-alt border-none text-[13px] font-manrope focus:outline-none focus:ring-1 focus:ring-paw-primary/30"
              data-testid="chatbot-input"
            />
            <button onClick={handleSend} className="w-9 h-9 rounded-lg bg-paw-primary hover:bg-paw-primary-hover text-white flex items-center justify-center transition-colors flex-shrink-0" data-testid="chatbot-send">
              <Send className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
